import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PlaybookDetail from '@/components/playbooks/PlaybookDetail';
import {
  AppButton,
  AppEmptyState,
  AppPage,
  AppText,
} from '../../ui-foundation';
import { GUIDE_SURFACE_ORDER, type GuideSurfaceSection } from '../../ui-foundation/features/guides';
import { useGuides } from '@/components/playbooks/guideData';

function parseGuideSection(value: string | undefined): GuideSurfaceSection | null {
  if (!value) return null;
  return GUIDE_SURFACE_ORDER.includes(value as GuideSurfaceSection) ? (value as GuideSurfaceSection) : null;
}

export default function GuideDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ section: string; kind: string; guideId: string }>();
  const section = parseGuideSection(params.section);
  const { guides, loading, error } = useGuides(section);

  const guide = useMemo(() => {
    if (!section || !params.kind || !params.guideId) return null;
    return guides.find((item) => item.kind === params.kind && item.id === params.guideId) || null;
  }, [guides, params.guideId, params.kind, section]);

  const fallbackPath = section ? `/guides?section=${section}` : '/guides';
  const returnPath = typeof location.state?.from === 'string' ? location.state.from : fallbackPath;

  if (!section) {
    return (
      <AppPage>
        <AppEmptyState title="Support item not found" body="This support route is missing its section." />
      </AppPage>
    );
  }

  if (loading) {
    return (
      <AppPage>
        <AppText color="$colorMuted" py="$lg">
          Loading support item...
        </AppText>
      </AppPage>
    );
  }

  if (error || !guide) {
    return (
      <AppPage>
        <AppEmptyState
          title="Support item not available"
          body={error || 'Adulting could not find this guide right now.'}
        />
        <AppButton tone="outline" onPress={() => navigate(returnPath)}>
          Back to support hub
        </AppButton>
      </AppPage>
    );
  }

  return (
    <AppPage className="animate-fade-in">
      <PlaybookDetail guide={guide} onBack={() => navigate(returnPath)} />
    </AppPage>
  );
}
